// Store integration layer - wires together actions and stores
import { addGlobalListener } from '../utils/actions.js';
import { dispatch, select, selectAll, subscribe, subscribeAll, subscribeToAllStores, clearStores, createStoreHandlers, hydrateStores } from '../utils/store.js';

// Module-level tracking for cleanup
let unsubscribeFromActions = null;

/**
 * Initialize the stores system by loading all store modules
 * @returns {Promise<void>}
 */
export async function initStores() {
  // Wire up the action system to the store system
  unsubscribeFromActions = addGlobalListener((actionContext, phase, ...args) => {
    dispatch(actionContext, phase, ...args);
  });
  
  // Auto-discover and register store files (exclude test files using negative glob pattern)
  const storeModules = import.meta.glob(['./**/*.js', '!./**/*.test.js', '!./index.js'], { 
    import: 'default'
  });
  
  // Load all modules
  const loadedModules = await Promise.all(
    Object.entries(storeModules).map(async ([filePath, importFn]) => {
      const module = await importFn();
      return [filePath, module];
    })
  );
  
  // Process each store file and wire up handlers
  loadedModules.forEach(([filePath, module]) => {
    // Extract path from file structure (e.g., './user.js' -> 'user', './profiles/media.js' -> 'profiles/media')
    const storePath = filePath
      .substring(2) // Remove './'
      .replace(/\.js$/, ''); // Remove '.js'
    
    // If the module exports a function, call it with store handlers
    if (typeof module === 'function') {
      const handlers = createStoreHandlers(storePath);
      module({ ...handlers, select });
    } else {
      console.warn(`Store file ${filePath} does not export a default function`);
    }
  });
  
  console.log('Stores initialized');
}

// Re-export the global store API
export { select, selectAll, subscribe, subscribeAll, subscribeToAllStores };

// Re-export store utilities for advanced usage
export { 
  dispatch,
  createStoreHandlers,
  getAllStores, 
  getAllStoreStates, 
  hasStore, 
  getStoreStats,
  clearStores,
  hydrateStores,
  excludeFromSync
} from '../utils/store.js';

// Cleanup function for hot reload
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    if (unsubscribeFromActions) {
      unsubscribeFromActions();
    }
    clearStores();
  });
}